import moment from "moment/moment";
import {Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip} from "@mui/material";
import Formatter from "../../util/formatter";
import config from "../../app/config";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import {useSelector} from "react-redux";
import {additionalData} from "../../store/reducers/additionalData";
import {useTranslation} from "react-i18next";
import makeStyles from "@mui/styles/makeStyles";
import {useEffect, useState} from "react";
import _ from "lodash";
import InformationBox from "../other/informationBox";
import {SepsisPathwayStatus} from '../../pages/ward/sepsis/sepsisUtil';

const useStyles = makeStyles((theme) => ({
    headCell: {
        fontSize: "12px",
        background: theme.palette.background.paper,
    },
    rowCell: {
        fontSize: "small",
    },
    greenIcon: {
        color: "green",
    },
    redIcon: {
        color: "red",
    },
    naResult: {
        color: "#FFA500",
        cursor: "help",
    },
}));

const SepsisTable = ({patient, onlyLast, showTableOnEmpty = true}) => {
    const classesLocal = useStyles();
    const {t} = useTranslation();

    const patientAddData = useSelector(additionalData);
    const [sepsis, setSepsis] = useState([]);
    const age = moment().diff(patient?.patient?.dob, "years");

    useEffect(() => {
        if (!_.isEmpty(patientAddData?.other?.sepsisPathways)) {
            const filteredSepsis = _.filter(patientAddData?.other?.sepsisPathways, sepsis => {
                return sepsis.statusObj !== SepsisPathwayStatus.CANCELED
                    && moment(sepsis.dateCreate).isAfter(moment(patient?.admission?.startDate))
                    && (patient?.admission?.endDate ? moment(sepsis.dateCreate).isSameOrBefore(moment(patient?.admission?.endDate)) : true);
            });
            let sortedSepsis = _.sortBy(filteredSepsis, (obj) => moment(obj.dateCreate)).reverse();
            if (onlyLast && !_.isEmpty(sortedSepsis)) sortedSepsis = [_.first(sortedSepsis)];
            setSepsis(sortedSepsis);
        }
    }, [patient, patientAddData, onlyLast]);

    const renderPaedsTable = () => {
        return (
            <TableContainer component={Paper}>
                <Table sx={{minWidth: 650}} aria-label="Sepsis pathway">
                    <TableHead>
                        <TableRow>
                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Created")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("User")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Oxygen")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Bp & Urin")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Iv/Io")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Blood")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Meds")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Fluid")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Status")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Complete/Stop")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("User")}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {sepsis.map(sep => (
                        <TableBody key={sep.id}>
                            <TableRow sx={{"&:last-child td, &:last-child th": {border: 0}}}>
                                <TableCell
                                    align="center"
                                    component="th"
                                    scope="row"
                                    size={"small"}
                                    className={classesLocal.rowCell}
                                >
                                    {Formatter.dateFormatted(sep.dateCreate, config.defaultDateTimeFormat)}
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.userCreate}
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.oxygen &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.oxygen === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.oxygen === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.oxygenNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.balance &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.balance === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.balance === false &&
                                        <Tooltip
                                            title={t("Reason") + ": " + sep.balanceNAReason}
                                            arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.ivio &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.ivio === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.ivio === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.ivioNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.bloods &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.bloods === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.bloods === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.bloodsNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.mbc &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.mbc === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.mbc === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.mbcNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.fluids &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.fluids === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.fluids === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.fluidsNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.statusObj === SepsisPathwayStatus.INCOMPLETE &&
                                        <span>{t("incomplete")}</span>
                                    }
                                    {sep.statusObj === SepsisPathwayStatus.CANCELED &&
                                        <Tooltip
                                            title={t("Reason") + ": " + sep.notRequiredReason}
                                            arrow>
                                            <span style={{cursor: "help"}}>{t("not required")}</span>
                                        </Tooltip>
                                    }
                                    {sep.statusObj === SepsisPathwayStatus.ENDED &&
                                        <span>{t("complete")}</span>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {Formatter.dateFormatted(sep.dateEnd, config.defaultDateTimeFormat)}
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.userEnd}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    ))}
                </Table>
            </TableContainer>
        );
    };

    const renderAdultTable = () => {
        return (
            <TableContainer component={Paper}>
                <Table sx={{minWidth: 650}}>
                    <TableHead>
                        <TableRow>
                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Created")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("User")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Oxygen")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Fluids")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Bloods")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("MBC")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Antibiotics")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Balance")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Status")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("Complete/Stop")}
                            </TableCell>

                            <TableCell size={"small"} className={classesLocal.headCell} align="center">
                                {t("User")}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {sepsis.map(sep => (
                        <TableBody key={sep.id}>
                            <TableRow>
                                <TableCell
                                    align="center"
                                    component="th"
                                    scope="row"
                                    size={"small"}
                                    className={classesLocal.rowCell}
                                >
                                    {Formatter.dateFormatted(sep.dateCreate, config.defaultDateTimeFormat)}
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align="center">
                                    {sep.userCreate}
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.oxygen &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.oxygen === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.oxygen === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.oxygenNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.fluids &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.fluids === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.fluids === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.fluidsNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.bloods &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.bloods === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.bloods === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.bloodsNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.mbc &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.mbc === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.mbc === false &&
                                        <Tooltip title={t("Reason") + ": " + sep.mbcNAReason}
                                                 arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.antibiotics &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.antibiotics === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.antibiotics === false &&
                                        <Tooltip
                                            title={t("Reason") + ": " + sep.antibioticsNAReason}
                                            arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.balance &&
                                        <CheckIcon className={classesLocal.greenIcon}/>
                                    }
                                    {sep.balance === null &&
                                        <ClearIcon className={classesLocal.redIcon}/>
                                    }
                                    {sep.balance === false &&
                                        <Tooltip
                                            title={t("Reason") + ": " + sep.balanceNAReason}
                                            arrow>
                                            <span className={classesLocal.naResult}>N/A</span>
                                        </Tooltip>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.statusObj === SepsisPathwayStatus.INCOMPLETE &&
                                        <span>{t("incomplete")}</span>
                                    }
                                    {sep.statusObj === SepsisPathwayStatus.CANCELED &&
                                        <Tooltip
                                            title={t("Reason") + ": " + sep.notRequiredReason}
                                            arrow>
                                            <span style={{cursor: "help"}}>{t("not required")}</span>
                                        </Tooltip>
                                    }
                                    {sep.statusObj === SepsisPathwayStatus.ENDED &&
                                        <span>{t("complete")}</span>
                                    }
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {Formatter.dateFormatted(sep.dateEnd, config.defaultDateTimeFormat)}
                                </TableCell>

                                <TableCell size={"small"} className={classesLocal.rowCell} align={"center"}>
                                    {sep.userEnd}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    ))}
                </Table>
            </TableContainer>
        );
    };

    const emptySepsisInfo = () => {
        return (
            (!_.isEmpty(sepsis) || (showTableOnEmpty && _.isEmpty(sepsis))) &&
            <InformationBox type={"info"}>
                {t("Sepsis Pathway was not made.")}
            </InformationBox>
        );
    };

    const renderSepsisTable = () => {
        if (_.isEmpty(sepsis)) return emptySepsisInfo();

        if (age < 18) return renderPaedsTable();
        return renderAdultTable();
    };

    return (
        <>
            {renderSepsisTable()}
        </>
    );
};

export default SepsisTable;